import React, { useEffect, useRef, useState } from 'react';
import { FaSpinner, FaExclamationTriangle } from 'react-icons/fa';
import { consumeMagicLink } from '../api/client';

/**
 * Landing page for the emailed sign-in link.
 *
 * Redeems the single-use token, stores the session, and hands control back to
 * the app. The visitor should experience this as "I clicked the link and I was
 * in" — there is nothing to fill in here.
 */
const MagicLinkLanding = ({ onSignedIn }) => {
  const [error, setError] = useState('');
  // The token is single-use, so redeeming it twice fails. React 18 StrictMode
  // mounts effects twice in development, which would burn the token on the
  // first mount and show an error on the second.
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    const token = new URLSearchParams(window.location.search).get('token');
    if (!token) {
      setError('That link is missing its sign-in code.');
      return;
    }

    consumeMagicLink(token)
      .then(async (user) => {
        // Strip the token from the URL before anything else can read it, so it
        // is not left sitting in history or leaked in a referrer header.
        window.history.replaceState({}, '', '/');
        if (onSignedIn) await onSignedIn(user);
      })
      .catch((err) => {
        setError(
          err?.response?.data?.detail ||
            'This sign-in link is no longer valid. Please request a new one.'
        );
      });
  }, [onSignedIn]);

  return (
    <div className="mx-auto flex min-h-[50vh] max-w-lg flex-col items-center justify-center px-6 text-center">
      {error ? (
        <>
          <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
            <FaExclamationTriangle className="text-xl text-amber-600" />
          </span>
          <h1 className="font-serif text-2xl font-bold text-neutral-900">
            That link has expired
          </h1>
          <p className="mt-2 text-neutral-600">{error}</p>
          <a
            href="/tryout"
            className="mt-6 inline-flex items-center rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white no-underline hover:bg-primary-700"
          >
            Plan a trip
          </a>
        </>
      ) : (
        <>
          <FaSpinner className="mb-4 animate-spin text-3xl text-primary-500" />
          <h1 className="font-serif text-2xl font-bold text-neutral-900">
            Signing you in…
          </h1>
          <p className="mt-2 text-neutral-600">One moment.</p>
        </>
      )}
    </div>
  );
};

export default MagicLinkLanding;
